import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Button, InputNumber, message, Select } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import CouponService from "src/module/coupon/service/coupon.service";
import subscriptionCouponService from "src/module/subscriptionCoupon/service";

interface IBaseCouponData {
	id: number;
	title: string;
	amount: number;
	[x: string]: any;
}

interface IValue {
	couponList: IBaseCouponData[];
	subscriptionCouponList: IBaseCouponData[];
}

interface ThisProps {
	value?: Partial<IValue>;
	onChange?: (value: Partial<IValue>) => void;
}

// 实际上此处并不需要接收value 多做了

const CouponSelector: FC<ThisProps> = (props) => {
	const [couponType, setCouponType] = useState<number | void>(void 0);
	const [couponNameIpt, setCouponNameIpt] = useState<string | undefined>(void 0);
	const [couponInfo, setCouponInfo] = useState<Record<string, any> | void>(void 0);
	const [couponNum, setCouponNum] = useState<number | null>(null);

	const [couponList, setCouponList] = useState<{ value: number; label: string }[]>([]);

	const [selectedCouponListObj, setSelectedCouponListObj] = useState<Partial<IValue>>({
		couponList: [],
		subscriptionCouponList: [],
	});

	const getSelectedCouponList = useMemo(() => {
		return [
			...(selectedCouponListObj.couponList || []).map((item) => {
				return { ...item, thisCouponType: 1 };
			}),
			...(selectedCouponListObj.subscriptionCouponList || []).map((item) => {
				return { ...item, thisCouponType: 2 };
			}),
		];
	}, [selectedCouponListObj]);
	const couponTypeOptions = [
		{
			label: "普通优惠券",
			value: 1,
		},
		{
			label: "会员卡优惠券",
			value: 2,
		},
	];

	const init = useCallback(() => {
		setSelectedCouponListObj(props.value || {});
	}, [props.value]);

	useEffect(() => {
		init();
	}, [init]);

	const changeCouponType = (value: number | void) => {
		setCouponInfo(void 0);
		setCouponNum(null);
		setCouponType(value);
	};

	const throttleFetchData = useCallback(async () => {
		if (!couponType) {
			return;
		}
		// 1调普通优惠券接口
		// 2调会员卡优惠券接口
		const getData = couponType === 1 ? CouponService.noAuth : subscriptionCouponService.list;
		const { data: res } = await getData({ title: couponNameIpt, type: 1, isDisplay: 1 });
		setCouponList(res.data.list.map((item: any) => ({ ...item, value: item.id, label: item.title, thisCouponType: couponType })));
	}, [couponNameIpt, couponType]);

	useEffect(() => {
		throttleFetchData();
	}, [throttleFetchData]);

	const getCouponInfo = (value: string) => {
		const coupon = couponList.find((item) => item.value === Number(value));
		setCouponInfo(coupon);
	};

	const getFinalData = (selCouponList: IBaseCouponData[]) => {
		return selCouponList.map((item) => {
			return {
				...(item.thisCouponType === 1 ? { couponId: item.id } : { subscriptionCouponId: item.id }),
				...(item.parentId && { parentId: item.parentId }),
				amount: item.amount,
			};
		});
	};

	const plusCoupon = () => {
		if (couponInfo?.thisCouponType !== couponType) {
			setCouponInfo(void 0);
			message.error("优惠券类型冲突");
			return;
		}
		if (!couponInfo?.id || !couponNum) {
			message.error("请选择优惠券和数量");
			return;
		}
		const haveSelectedThisCoupon = getSelectedCouponList.find((item) => item.thisCouponType === couponType && item.id === couponInfo?.id);
		if (haveSelectedThisCoupon) {
			message.error("优惠券已选择");
			return;
		}
		const newObj = {
			...selectedCouponListObj,
			[couponType === 1 ? "couponList" : "subscriptionCouponList"]: [
				...(selectedCouponListObj[couponType === 1 ? "couponList" : "subscriptionCouponList"] || []),
				{ ...couponInfo, amount: couponNum || 0 } as IBaseCouponData,
			],
		};
		props.onChange?.(newObj);
		setCouponInfo(void 0);
		setCouponNum(null);
		setCouponType(void 0);
	};

	const minusCoupon = (item: IBaseCouponData) => {
		const key = item.thisCouponType === 1 ? "couponList" : "subscriptionCouponList";
		const newList = selectedCouponListObj[key]!.filter((coupon) => coupon.id !== item.id);
		setSelectedCouponListObj({
			...selectedCouponListObj,
			[key]: newList,
		});
		props.onChange?.({
			...selectedCouponListObj,
			[key]: newList,
		});
	};

	return (
		<Container>
			<div className='selectContainer'>
				<Select placeholder='优惠券类型' className='couponSelectorItem' options={couponTypeOptions} value={couponType} onChange={changeCouponType} />
				<Select
					placeholder='优惠券名称'
					className='couponSelectorItem specialItem'
					showSearch
					allowClear
					onSearch={setCouponNameIpt}
					value={couponInfo?.id || void 0}
					options={couponList}
					optionFilterProp={"label"}
					onChange={getCouponInfo}
				/>
				<InputNumber placeholder='数量' className='couponSelectorItem' value={couponNum} onChange={setCouponNum} min={0} />
				<Button type='text' onClick={plusCoupon}>
					<PlusOutlined style={{ color: "#1677ff" }} />
				</Button>
			</div>
			<div className='selectValueContainer'>
				{getSelectedCouponList.map((item) => {
					return (
						<div key={item.id} className='selectValueItem'>
							<p className='itemType item'>{item.thisCouponType === 1 ? "普通优惠券" : "会员卡优惠券"}</p>
							<p className='itemName item'>{item.title}</p>
							<p className='itemAmount item'>X {item.amount}</p>
							<Button type='text' onClick={() => minusCoupon(item)}>
								<MinusOutlined style={{ color: "#ff4d4f" }} />
							</Button>
						</div>
					);
				})}
			</div>
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	height: 100%;

	.selectContainer {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
		.couponSelectorItem {
			width: 21%;
		}
		.specialItem {
			width: 50%;
		}
	}
	.selectValueContainer {
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		.selectValueItem {
			width: 100%;
			height: 30px;
			display: flex;
			align-items: center;
			justify-content: space-between;
			.item {
				box-sizing: border-box;
				padding-left: 10px;
			}
			.itemName {
				width: 50%;
				/* padding-left: 20px; */
			}
			.itemAmount {
				width: 20%;
			}
			.itemType {
				width: 20%;
			}
		}
	}
`;

export default CouponSelector;
